import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface SnilsInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlurValidate?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Компонент ввода СНИЛС.
 * Формат: XXX-XXX-XXX XX (11 цифр)
 */
const SnilsInput = forwardRef<HTMLInputElement, SnilsInputProps>(
  ({ value, onChange, onBlurValidate, placeholder = "000-000-000 00", className, disabled = false }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      setDisplayValue(value || "");
    }, [value]);

    const formatSnils = (input: string, prevValue: string): string => {
      // Разрешаем цифры, дефисы и пробелы
      const cleaned = input.replace(/[^\d- ]/g, "");

      // Если пользователь удаляет символы, не форматируем
      if (cleaned.length < prevValue.length) {
        return cleaned.slice(0, 14);
      }

      // Автоформатирование
      const digits = cleaned.replace(/[-\s]/g, "");
      let formatted = "";

      for (let i = 0; i < digits.length && i < 11; i++) {
        // Добавляем дефисы после 3-й и 6-й цифры
        if (i === 3 || i === 6) {
          formatted += "-";
        }
        // Добавляем пробел после 9-й цифры
        if (i === 9) {
          formatted += " ";
        }
        formatted += digits[i];
      }

      return formatted;
    };

    const validateSnils = (snilsStr: string): string | null => {
      const digits = snilsStr.replace(/[-\s]/g, "");

      if (!digits || digits.length < 11) {
        return null;
      }

      if (digits.length !== 11) {
        return "СНИЛС должен содержать 11 цифр";
      }

      // Проверка контрольной суммы для номеров >= 001-001-998
      const number = parseInt(digits.slice(0, 9), 10);
      const checkSum = parseInt(digits.slice(9, 11), 10);

      // Номера меньше 001-001-998 не проверяются по контрольной сумме
      if (number <= 1001998) {
        return null;
      }

      // Расчет контрольной суммы
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += parseInt(digits[i], 10) * (9 - i);
      }

      let calculatedCheckSum: number;
      if (sum < 100) {
        calculatedCheckSum = sum;
      } else if (sum === 100 || sum === 101) {
        calculatedCheckSum = 0;
      } else {
        calculatedCheckSum = sum % 101;
        if (calculatedCheckSum === 100) {
          calculatedCheckSum = 0;
        }
      }

      if (calculatedCheckSum !== checkSum) {
        return "Неверная контрольная сумма СНИЛС";
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatSnils(input, displayValue);

      setDisplayValue(formatted);
      onChange(formatted);

      // Валидация полного СНИЛС (14 символов с разделителями)
      if (formatted.length === 14) {
        const validationError = validateSnils(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < 14) {
        setError("Введите полный СНИЛС");
      } else if (displayValue.length === 14 && !error && onBlurValidate) {
        onBlurValidate(displayValue);
      }
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={14}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:border-red-500 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

SnilsInput.displayName = "SnilsInput";

export default SnilsInput;
